import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../../createAppAsyncThunk';
import { API_POST } from '../../../services';
import { SalesPerformanceDetailsByFulfillment } from '../../../models/SalesPerformanceDetailsByFulfillmentModel';
import { RADRequest } from '../../../models/common/RADRequest';
import type { RootState } from '../../store';
import { env } from '../../../env';
import { ENDPOINTS } from '../../../constants/endPoints';

interface EcommSalesPerfState {
  salesByFulfillment: SalesPerformanceDetailsByFulfillment | null;
  isLoading: boolean;
  // Mode the loaded data was fetched for (false = single, true = multi, null = not loaded yet).
  // The header shows the peer group name only when this matches the active mode, so the previous
  // mode's name doesn't flash while a mode switch re-fetches.
  loadedForMultiStore: boolean | null;
}

const initialState: EcommSalesPerfState = {
  salesByFulfillment: null,
  isLoading: false,
  loadedForMultiStore: null,
};

export const fetchEcommSalesPerformance = createAppAsyncThunk(
  'ecommSalesPerf/fetchSalesPerformance',
  async (params: RADRequest) => {
    const payload = {
      storeNumber: params.storeNumber,
      isMultiStoreMode: params.isMultiStoreMode ?? false,
    };

    const response = await API_POST(
      `${env.RAD_APP_API_URL}${ENDPOINTS.GET_ECOMM_SALES_PERFORMANCE}`,
      payload,
    );
    return response as SalesPerformanceDetailsByFulfillment;
  },
);

const ecommSalesPerfSlice = createSlice({
  name: 'ecommSalesPerf',
  initialState,
  reducers: {
    clearSalesPerformance: state => {
      state.salesByFulfillment = null;
      state.loadedForMultiStore = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEcommSalesPerformance.pending, state => {
        state.isLoading = true;
        state.salesByFulfillment = null;
        state.loadedForMultiStore = null;
      })
      .addCase(fetchEcommSalesPerformance.fulfilled, (state, action) => {
        state.salesByFulfillment = action.payload;
        state.isLoading = false;
        state.loadedForMultiStore = action.meta.arg.isMultiStoreMode ?? false;
      })
      .addCase(fetchEcommSalesPerformance.rejected, state => {
        state.salesByFulfillment = null;
        state.isLoading = false;
        state.loadedForMultiStore = null;
      });
  },
});

export const { clearSalesPerformance } = ecommSalesPerfSlice.actions;

// Selectors
export const selectSalesByFulfillment = (state: RootState) =>
  state.ecommSalesPerf.salesByFulfillment;
export const selectEcommLoading = (state: RootState) => state.ecommSalesPerf.isLoading;

// Returns the peer group / multi-store group name shown in the header banner, or null if no
// data is loaded. It comes from the sales-by-fulfillment response, which is fetched per mode,
// so the name already matches whichever mode (single or multi-store) was requested.
export const selectPeerGroupDescription = (state: RootState) =>
  state.ecommSalesPerf.salesByFulfillment?.peerGroupDescription ?? null;

// Returns the mode the loaded peer group name was fetched for (false = single-store,
// true = multi-store, null = loading/unloaded). The header compares this against the active
// mode before showing the name, so a stale name from the previous mode never shows mid-switch.
export const selectPeerGroupLoadedMode = (state: RootState) =>
  state.ecommSalesPerf.loadedForMultiStore;

export default ecommSalesPerfSlice.reducer;
